import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import EditableImage from "../../core/EditableImage";
import { PiStarFill, PiQuotesFill, PiPlus, PiTrash, PiUserCircle, PiDotsSix } from "react-icons/pi";

export default function TestimonialsDark({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    // Safety check for testimonials array
    const testimonials = content.testimonials || [
        {
            name: "Sarah Johnson",
            role: "Marketing Director",
            company: "TechCorp Inc.",
            text: "This platform has completely transformed how we manage our marketing campaigns. The intuitive interface and powerful features have boosted our productivity by 40%.",
            image: ""
        },
        {
            name: "Michael Chen",
            role: "CEO",
            company: "StartupXYZ",
            text: "Incredible value for the price. The customer support is exceptional and the platform continues to evolve with our needs.",
            image: ""
        },
        {
            name: "Emily Rodriguez",
            role: "Product Manager",
            company: "Innovate Labs",
            text: "The best decision we made this year was switching to this solution. Our team's efficiency has improved dramatically.",
            image: ""
        }
    ];

    const addTestimonial = () => {
        const newItems = [...testimonials, {
            name: "New Client",
            role: "Role",
            company: "Company",
            text: "Share your experience...",
            image: ""
        }];
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const removeTestimonial = (idx: number) => {
        const newItems = testimonials.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const updateTestimonial = (idx: number, field: string, value: string) => {
        const newItems = [...testimonials];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const [activeCard, setActiveCard] = useState<number | null>(null);

    return (
        <div
            className={`bg-[#0F172A] py-20 px-4 sm:px-6 lg:px-8 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                        Testimonials
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
                        <EditableText
                            value={content.title || "Trusted by Industry Leaders"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    {content.subtitle && (
                        <p className="text-xl text-gray-400 font-light leading-relaxed mb-4">
                            <EditableText
                                value={content.subtitle}
                                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                                as="span"
                                placeholder="Subtitle"
                            />
                        </p>
                    )}
                    {content.description && (
                        <p className="text-gray-500 max-w-2xl mx-auto">
                            <EditableText
                                value={content.description}
                                onChange={(val) => onContentChange?.({ ...content, description: val })}
                                as="span"
                                placeholder="Description text..."
                            />
                        </p>
                    )}
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {testimonials.map((item: any, idx: number) => (
                        <div
                            key={idx}
                            className="group relative flex flex-col bg-[#1E293B] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-800 hover:border-blue-500/30"
                            onMouseEnter={() => setActiveCard(idx)}
                            onMouseLeave={() => setActiveCard(null)}
                        >
                            {isSelected && (
                                <div className="absolute top-3 right-3 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeTestimonial(idx); }}
                                        className="h-8 w-8 flex items-center justify-center rounded-full bg-red-500/90 text-white shadow-sm hover:bg-red-600 transition-colors"
                                        title="Remove"
                                    >
                                        <PiTrash className="h-4 w-4" />
                                    </button>
                                </div>
                            )}

                            {/* Decorative top gradient bar */}
                            <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

                            {/* Image Section - Circular Avatar Style */}
                            <div className="p-8 pb-4">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="relative">
                                        <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-blue-500/30">
                                            <EditableImage
                                                value={item.image}
                                                onChange={(val) => updateTestimonial(idx, "image", val)}
                                                alt={item.name}
                                                width={64}
                                                height={64}
                                                className="w-16 h-16"
                                            />
                                        </div>
                                        {item.image && (
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-[#1E293B]" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white text-lg">
                                            <EditableText
                                                value={item.name}
                                                onChange={(val) => updateTestimonial(idx, "name", val)}
                                                as="span"
                                                placeholder="Client Name"
                                            />
                                        </div>
                                        <div className="text-blue-400 text-sm font-medium">
                                            <EditableText
                                                value={item.role}
                                                onChange={(val) => updateTestimonial(idx, "role", val)}
                                                as="span"
                                                placeholder="Role"
                                            />
                                        </div>
                                        {item.company && (
                                            <div className="text-gray-500 text-xs">
                                                <EditableText
                                                    value={item.company}
                                                    onChange={(val) => updateTestimonial(idx, "company", val)}
                                                    as="span"
                                                    placeholder="Company"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Star Rating */}
                                <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <PiStarFill key={star} className="w-4 h-4 text-yellow-400" />
                                    ))}
                                </div>
                            </div>

                            {/* Content Section */}
                            <div className="px-8 pb-8 flex-1 flex flex-col">
                                <div className="relative mb-4">
                                    <PiQuotesFill className="w-8 h-8 text-blue-500/20 absolute -top-2 -left-1" />
                                    <blockquote className="flex-1 pl-4">
                                        <p className="text-gray-300 leading-relaxed">
                                            <EditableText
                                                value={item.text}
                                                onChange={(val) => updateTestimonial(idx, "text", val)}
                                                as="span"
                                                placeholder="Testimonial text"
                                            />
                                        </p>
                                    </blockquote>
                                </div>

                                {/* Card footer decoration */}
                                <div className="mt-auto pt-4 border-t border-gray-700/50 flex items-center justify-between">
                                    <div className="text-xs text-gray-500">
                                        Verified Client
                                    </div>
                                    <div className={`transition-transform duration-300 ${activeCard === idx ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'}`}>
                                        <span className="text-blue-400 text-sm font-medium">Read more →</span>
                                    </div>
                                </div>
                            </div>

                            {/* Hover glow effect */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                        </div>
                    ))}

                    {isSelected && (
                        <button
                            onClick={addTestimonial}
                            className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-700 bg-[#1E293B]/30 p-8 text-gray-500 hover:border-blue-500 hover:text-blue-500 hover:bg-[#1E293B]/50 transition-all group"
                        >
                            <div className="w-16 h-16 rounded-full bg-gray-800 group-hover:bg-blue-500/10 flex items-center justify-center mb-4 transition-colors">
                                <PiPlus className="h-8 w-8" />
                            </div>
                            <span className="text-lg font-medium">Add Testimonial</span>
                            <span className="text-sm text-gray-600 mt-1">Click to add a new review</span>
                        </button>
                    )}
                </div>

                {/* Trust indicators */}
                <div className="mt-16 pt-8 border-t border-gray-800">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center">
                        <div className="text-gray-500 text-sm">
                            Trusted by <span className="text-white font-semibold">2,000+</span> companies worldwide
                        </div>
                        <div className="flex items-center gap-6 opacity-50">
                            {/* Simple star icons to represent trusted brands */}
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <PiStarFill key={i} className="w-4 h-4 text-white" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
